import { Help, Share } from "@mui/icons-material";
import { Box, Typography, useTheme } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { CHAIN_CHECK_LIST, CODEX_NETWORK_ID, Chain } from "chainConfigs";
import { TooltipTrigger } from "components/Tooltip/styled";
import { Value } from "components/Value";
import { HEADER_HEIGHT, ROUTES } from "consts";
import { useCheckViewPoint, VIEW_POINT_CONFIG } from "hooks/useCheckViewPoint";
import { useTooltips } from "hooks/useTooltips";
import { checkIsMobile } from "lib/utils";
import {
  DNA_ATTRIBUTE_CONFIG_MAP,
  DNA_CLASS_CONFIG_MAP,
  getScoreLevel,
  MAX_TRAIT_SCORE,
} from "pages/cabal-id/dna/dnaConfig";

import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosService from "services/axios";
import useLayoutConfigStore from "store/layout-config-store/useLayoutConfigStore";
import useToastStore from "store/toast-store/useToastStore";
import useUserStoreV2 from "store/user-store-v2/useUserStoreV2";
import { copyToClipboard, LANGUAGES } from "utils";
import { AppSideBar } from "v2/components/AppSideBar/AppSideBar";
import { CircleButton } from "v2/components/base/Button/ui";
import Divider from "v2/components/base/Divider/ui";
import withGradientBorder from "v2/components/base/Flex/hocs/withGradientBorder";
import { Card, Circle, Col, Row } from "v2/components/base/Flex/ui";
import { useModel } from "v2/components/base/Model/hooks/useModel";
import ScrollableEmpty from "v2/components/base/Scrollable/ui/ScrollableEmpty/v1";
import ScrollableLoading from "v2/components/base/Scrollable/ui/ScrollableLoading/v1";
import useTabs from "v2/components/base/Tabs/hooks/useTabs";
import Tabs from "v2/components/base/Tabs/ui";
import BasicButton from "v2/components/Buttons/BasicButton";
import CoinAvatar from "v2/components/CoinAvatar/ui";
import { UnsupportTokenModal } from "v2/components/Models/UnsupportTokenModal";
import { PercentageBlock, XPerformanceBlock } from "v2/components/NumBlock/ui";
import DefinitionTooltip from "v2/components/Tooltip/ui";
import DetailPageLayout from "v2/layouts/DetailPageLayout";
import RootLayout from "v2/layouts/RootLayout";
import SubPageLayout from "v2/layouts/SubPageLayout";

const tooltipMap = {
  earlyScore: "Spots winners before the crowd",
  accuracyScore: "Consistently calls tokens that go up",
  socialScore: "Commands strong social engagement",
  pumpScore: "Drives high volume and fast action in the market",
  insightScore: "Offers deep project and narrative analysis",
};

const GroupIdPage = () => {
  const navigate = useNavigate();
  const { isShowFooter, setIsShowFooter } = useLayoutConfigStore();
  const isMobile = useCheckViewPoint(VIEW_POINT_CONFIG.MOBILE);
  const { id } = useParams();
  const { data: jettonData, isLoading: isJettonDataLoading } = useQuery({
    queryKey: ["telegram-group", id],
    queryFn: () => axiosService.getTelegramGroupById({ id: id! }),
    enabled: !!id,
  });

  useEffect(() => {
    if (isMobile && isShowFooter) {
      setIsShowFooter(false);
    }
  }, [isMobile]);

  const handleBack = () => {
    navigate(ROUTES.explorer);
  };

  return (
    <SubPageLayout handleBack={handleBack} backToTitle="Back">
      {isJettonDataLoading ? (
        <Col type="cc" sx={{ height: "100vh" }} fullWidth>
          <ScrollableLoading />
        </Col>
      ) : !jettonData ? (
        <Col type="cc" sx={{ height: "100vh" }} fullWidth>
          <ScrollableEmpty />
        </Col>
      ) : jettonData ? (
        <DetailPageLayout
          isSubPage
          FixedContent={<GroupOverview groupData={jettonData} />}
          ScrollableContent={<GroupDetails groupData={jettonData} cabalId={id as string} />}
        />
      ) : null}
    </SubPageLayout>
  );
};

const GroupOverview = ({ groupData }: { groupData: any }) => {
  const theme = useTheme();
  const { showToast } = useToastStore();
  const isMobile = useCheckViewPoint(VIEW_POINT_CONFIG.MOBILE);
  const { tgUserName, tgUserId, getTgUserIdV2 } = useUserStoreV2();
  const totalSentimentCount = Object.values(groupData.sentimentBreakdown).reduce(
    (acc: any, curr: any) => acc + curr,
    0,
  ) as number;

  const handleJoinGroup = async () => {
    if (groupData.inviteLink) {
      window.open(groupData.inviteLink, "_blank");
    } else {
      showToast({ variant: "error", message: "No invite link found", duration: 3000 });
    }
  };

  return (
    <>
      <Col section="group-overview" type="ss" minHeight="100%" fullWidth fullHeight gap="24px">
        <Row section="group-overview-header" type="bc" fullWidth>
          <Row section="group-info" type="sc" gap="18px" fullWidth>
            <Col section="group-info-image" type="ss">
              <Circle.Image size={54} src={groupData.imageUri} />
            </Col>
            <Col section="group-info-name" type="ss" gap="10px">
              <Typography variant="mobile-h3">{groupData.name}</Typography>
              <Card.Badge>
                <Typography variant="mobile-body-s-heavy">
                  {LANGUAGES[groupData.language as keyof typeof LANGUAGES]}
                </Typography>
              </Card.Badge>
            </Col>
          </Row>
          <BasicButton
            variant="black"
            onClick={async () => {
              // For Mini App only
              const isMobile = checkIsMobile();
              if (!!navigator.share && isMobile) {
                try {
                  await navigator.share({
                    url: window.location.href,
                  });
                } catch (e) {
                  console.error("Error sharing the link", e);
                }
              } else {
                const url = new URL(window.location.href);
                url.searchParams.set("referralId", String(tgUserId || ""));
                copyToClipboard(url.toString());
                showToast({
                  variant: "success",
                  message: "Copied shared link to clipboard.",
                  duration: 3000,
                });
              }
            }}>
            <Share />
          </BasicButton>
        </Row>
        <Card.Section
          sx={{
            backgroundImage: `url("/group-id-overview-bg.png")`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            width: "100%",
            padding: "0px",
          }}>
          <Col
            section="group-overview-stats"
            type="ss"
            sx={{
              gap: "10px",
              padding: "8px",
              backdropFilter: "brightness(0.7)",
            }}
            fullWidth>
            <Card.Badge sx={{ width: "fit-content" }}>
              <Typography variant="mobile-body-s-heavy">
                {groupData.membersCount} Active Members
              </Typography>
            </Card.Badge>
            <Col
              type="cc"
              sx={{
                position: "relative",
                borderRadius: "300px",
                padding: "40px 8px",
                gap: "2px",

                "&::before": {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  borderRadius: "inherit",
                  padding: "2px",
                  background:
                    "linear-gradient(90deg, rgb(255, 174, 0), #FFFFFF, rgb(255, 174, 0), #FFFFFF)",
                  backgroundPosition: "center",
                  mask: "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0) content-box",
                  maskComposite: "exclude",
                  WebkitMaskComposite: "exclude",
                  "@keyframes gradientMove": {
                    "0%": {
                      backgroundSize: "250% 500%",
                    },
                    "50%": {
                      backgroundSize: "100% 200%",
                    },
                    "100%": {
                      backgroundSize: "250% 500%",
                    },
                  },
                  animation: "gradientMove 3s ease infinite",
                },
              }}
              fullWidth>
              <Typography variant="mobile-h2">Earned</Typography>
              <Row type="cc" gap="8px">
                <Typography variant="mobile-display-m" color="brand.primary">
                  {parseInt(groupData.shills)}
                </Typography>
                <Card.Badge>
                  <Typography variant="mobile-body-s-heavy">Shills</Typography>
                </Card.Badge>
              </Row>
            </Col>
            <Card.Badge sx={{ width: "fit-content" }}>
              <Typography variant="mobile-body-s-heavy" color="system.green-alarm">
                {`+${Math.round(groupData.change)} Today`}
              </Typography>
            </Card.Badge>
          </Col>
        </Card.Section>
        <Card.Section sx={{ borderRadius: "16px", maxWidth: "100%", width: "100%" }}>
          <Col
            section="group-overview-stats"
            type="ss"
            sx={{
              gap: "10px",
              maxWidth: "100%",
            }}
            fullWidth>
            <Typography variant="mobile-body-s">Sentiment Past 7 Days</Typography>
            <Row
              section="group-overview-sentiment"
              sx={{
                maxWidth: "100%",
                overflowX: "scroll",
                gap: "8px",
              }}
              fullWidth>
              {["Bullish", "Neutral", "Bearish"].map((sentiment) => {
                return (
                  <SentimentBadge
                    sentiment={sentiment}
                    value={
                      groupData.sentimentBreakdown[sentiment.toLowerCase()] !== 0
                        ? groupData.sentimentBreakdown[sentiment.toLowerCase()] /
                          totalSentimentCount
                        : 0
                    }
                  />
                );
              })}
            </Row>
            <Divider.Row />
            <Typography variant="mobile-body-s">Pre-TGE Radar</Typography>
            <Row
              section="group-overview-most-mentioned-protocols"
              sx={{
                maxWidth: "100%",
                gap: "8px",
                overflowX: "scroll",
              }}>
              {groupData.mostMentionedProtocols.length > 0 ? (
                groupData.mostMentionedProtocols.map((protocol: string) => {
                  return (
                    <Card.Badge>
                      <Typography variant="mobile-body-s">{protocol}</Typography>
                    </Card.Badge>
                  );
                })
              ) : (
                <Typography variant="mobile-body-s">N/A</Typography>
              )}
            </Row>
          </Col>
        </Card.Section>
        <JoinCommunityButton
          isMobile={isMobile}
          groupData={groupData}
          handleJoinGroup={handleJoinGroup}
        />
        {/* <BasicButton
        variant={!groupData.inviteLink ? "disabled" : "primary"}
        sx={{ position: "fixed", bottom: "42px", left: "42px", width: "calc(100% - 84px)" }}
        onClick={handleJoinGroup}
        disabled={!groupData.inviteLink}>
        <Typography
          variant="mobile-action-s-heavy"
          color={!groupData.inviteLink ? "text.secondary" : "text.brand"}>
          Join Community
        </Typography>
      </BasicButton> */}
      </Col>
    </>
  );
};

const JoinCommunityButton = ({
  isMobile,
  groupData,
  handleJoinGroup,
}: {
  isMobile: boolean;
  groupData: any;
  handleJoinGroup: () => void;
}) => {
  return (
    <BasicButton
      variant={!groupData.inviteLink ? "disabled" : "primary"}
      sx={{
        ...(isMobile && {
          position: "fixed",
          bottom: "24px",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 1000,
          width: "100%",
        }),
        ...(!isMobile && {
          width: "100%",
        }),
      }}
      onClick={handleJoinGroup}
      disabled={!groupData.inviteLink}>
      <Typography
        variant="mobile-action-s-heavy"
        color={!groupData.inviteLink ? "text.secondary" : "text.brand"}>
        Join Community
      </Typography>
    </BasicButton>
  );
};

const SentimentBadge = ({ sentiment, value }: { sentiment: string; value: number }) => {
  const theme = useTheme();
  const color =
    sentiment === "Bullish"
      ? theme.palette.system["green-alarm"]
      : sentiment === "Neutral"
      ? theme.palette.text.primary
      : theme.palette.system["red-alarm"];

  return (
    <Card.Badge>
      <Row type="cc" gap="4px" padding="2px 0px">
        <Row
          sx={{
            width: "10px",
            height: "10px",
            borderRadius: "50%",
            backgroundColor: color,
          }}
        />
        <Typography variant="mobile-body-s-heavy" color={color}>
          {sentiment}
        </Typography>
        <Row
          sx={{
            width: "4px",
            height: "4px",
            borderRadius: "50%",
            backgroundColor: "#FFF",
          }}
        />
        <Typography variant="mobile-body-s" color="text.primary">
          {Math.round(value * 100)}%
        </Typography>
      </Row>
    </Card.Badge>
  );
};

const GroupDetails = ({ groupData, cabalId }: { groupData: any; cabalId: string }) => {
  const theme = useTheme();
  const isMobile = useCheckViewPoint(VIEW_POINT_CONFIG.MOBILE);
  const { selectedTab, handleTabChange } = useTabs(1);
  const { isOpen, open, close } = useModel({
    modelId: "unsupport-token-modal",
  });

  const filteredCabalCalls = useMemo(() => {
    if (groupData.cabalCall) {
      return groupData.cabalCall.filter((call: any) => {
        const THRESHOLD = 0.01;
        return !(call.multiplier < 1 + THRESHOLD || call.marketCap <= 0);
      });
    } else {
      return [];
    }
  }, [groupData.cabalCall]);

  const filteredCabalCalls7Days = useMemo(() => {
    if (groupData.cabalCall7Days) {
      return groupData.cabalCall7Days.filter((call: any) => {
        const THRESHOLD = 0.01;
        return !(call.multiplier < 1 + THRESHOLD || call.marketCap <= 0);
      });
    } else {
      return [];
    }
  }, [groupData.cabalCall7Days]);

  return (
    <>
      <Col section="group-details" type="ss" gap="20px" fullWidth>
        <Col section="group-details-cabal-class" gap="8px" fullWidth>
          <Typography variant="mobile-body-m-heavy">Cabal Class</Typography>
          <Card.ImageSection
            imageUrl={`/group-class-${groupData.groupClass}.png`}
            sx={{
              width: "100%",
              minHeight: "320px",
              padding: "40px 8px 120px",
            }}>
            <Col type="sc" gap="27px" fullWidth>
              <Col type="sc" gap="10px" fullWidth>
                <Typography variant="mobile-h2">
                  {
                    DNA_CLASS_CONFIG_MAP[groupData.groupClass as keyof typeof DNA_CLASS_CONFIG_MAP]
                      .name
                  }
                </Typography>
                <Typography variant="mobile-display-xs" color="text.secondary">
                  {
                    DNA_CLASS_CONFIG_MAP[groupData.groupClass as keyof typeof DNA_CLASS_CONFIG_MAP]
                      .subtitle
                  }
                </Typography>
              </Col>
              <Col type="sc" gap="10px" fullWidth>
                <Typography variant="mobile-body-s">Main Traits</Typography>
                {Object.entries(groupData.traitScores)
                  // .filter(([key]) => key !== "insightScore")
                  .sort(
                    (a, b) =>
                      groupData.traitScores[b[0] as keyof typeof groupData.traitScores] -
                      groupData.traitScores[a[0] as keyof typeof groupData.traitScores],
                  )
                  .map(([key, value]) => {
                    return <DNAAttributeItem keyName={key} value={value as number} />;
                  })}
              </Col>
            </Col>
          </Card.ImageSection>
        </Col>
        <Col section="group-details-cabal-class" gap="8px" fullWidth>
          <Typography variant="mobile-body-m-heavy">Key Performance</Typography>
          <Card.Section sx={{ width: "100%" }}>
            <Col section="group-details-key-performance" type="ss" gap="24px" fullWidth>
              <Typography variant="mobile-body-s">Average Performance</Typography>
              <Col section="group-details-key-performance-blocks" type="ss" fullWidth>
                <Row type="bc" fullWidth>
                  <Typography variant="mobile-body-m" color={theme.palette.text.secondary}>
                    7 Days
                  </Typography>
                  <PercentageBlock percentage={groupData.day7Performance} />
                </Row>
                <Divider.Row />
                <Row type="bc" fullWidth>
                  <Typography variant="mobile-body-m" color={theme.palette.text.secondary}>
                    14 Days
                  </Typography>
                  <PercentageBlock percentage={groupData.day14Performance} />
                </Row>
              </Col>
            </Col>
          </Card.Section>
        </Col>
        <Col section="group-details-best-token-calls" gap="8px" fullWidth>
          <Typography variant="mobile-body-m-heavy">Best Token Calls</Typography>
          <Card.Section sx={{ width: "100%" }}>
            <Tabs.Group
              tabs={["14 Days", "7 Days"]}
              selectedTab={selectedTab}
              handleTabChange={handleTabChange}
            />
            <Row marginY="8px" />
            <Tabs.Body index={0} selectedTab={selectedTab}>
              <Col type="ss" sx={{ gap: "12px" }} fullWidth>
                {filteredCabalCalls.length ? (
                  <Col type="ss" sx={{ gap: "12px", minHeight: "249px" }} fullWidth>
                    {filteredCabalCalls.map((call: any) => {
                      return (
                        <CabalCallDetail
                          call={call}
                          xPerformance={call.multiplier}
                          setIsModalOpen={open}
                          cabalId={cabalId}
                        />
                      );
                    })}
                  </Col>
                ) : (
                  <NoCallsView />
                )}
              </Col>
            </Tabs.Body>
            <Tabs.Body index={1} selectedTab={selectedTab}>
              <Col type="ss" gap="12px" fullWidth>
                {filteredCabalCalls7Days.length ? (
                  <Col type="ss" sx={{ gap: "12px", minHeight: "249px" }} fullWidth>
                    {filteredCabalCalls7Days.map((call: any) => {
                      return (
                        <CabalCallDetail
                          call={call}
                          xPerformance={call.multiplier}
                          setIsModalOpen={open}
                          cabalId={cabalId}
                        />
                      );
                    })}
                  </Col>
                ) : (
                  <NoCallsView />
                )}
              </Col>
            </Tabs.Body>
            {isMobile ? <Row sx={{ minHeight: "80px" }} /> : null}
          </Card.Section>
        </Col>
      </Col>
      {/* <DefinitionTooltip term={title} isShow={isTooltipOpen} maxWidth="480px">
      <Typography variant="body-default">{tooltipText}</Typography>
    </DefinitionTooltip> */}
      <UnsupportTokenModal open={isOpen} onClose={close} />
    </>
  );
};

const CabalCallDetail = ({
  call,
  xPerformance,
  setIsModalOpen,
  cabalId,
}: {
  call: any;
  xPerformance: number;
  setIsModalOpen: (isModalOpen: boolean) => void;
  cabalId: string;
}) => {
  const navigate = useNavigate();
  const theme = useTheme();

  const navigateToTokenPage = (address: string, chainId: number) => {
    const chain =
      typeof chainId === "string"
        ? chainId
        : CODEX_NETWORK_ID[chainId as keyof typeof CODEX_NETWORK_ID];
    const isSupportedChain = CHAIN_CHECK_LIST.includes(chain as Chain);

    if (!isSupportedChain) {
      setIsModalOpen(true);
      console.error("Invalid chain", chain);
      return;
    }

    navigate(`/token/${address}?from=cabal&chain=${chain}&cabalId=${cabalId}`);
  };

  return (
    <Row
      section="group-details-best-token-calls-blocks"
      type="bc"
      fullWidth
      sx={{
        cursor: "pointer",
        gap: theme.spacing(1),
      }}
      onClick={() => navigateToTokenPage(call.address, call.chain)}>
      <Row type="sc" gap="12px">
        <CoinAvatar
          size={40}
          badgeSize={20}
          iconUri={call.iconUri}
          chain={
            typeof call.chain === "string"
              ? call.chain
              : (CODEX_NETWORK_ID[call.chain as keyof typeof CODEX_NETWORK_ID] as Chain)
          }
        />
        <Col type="ss" gap="4px">
          <Row
            type="sc"
            sx={{
              maxWidth: "calc(100vw - 225px)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}>
            <Typography variant="mobile-body-s-heavy">{call.name}</Typography>
          </Row>
          {/* {call?.marketCap > 0 && (
            <Row type="sc" sx={{ width: "fit-content" }}>
              <Typography variant="mobile-body-xs">
                CALLED AT <Value variant="mobile-body-xs" value={call.marketCap} /> MCAP
              </Typography>
            </Row>
          )} */}
        </Col>
      </Row>
      <XPerformanceBlock xPerformance={xPerformance} />
    </Row>
  );
};

const NoCallsView = () => {
  const theme = useTheme();
  return (
    <Row
      type="cc"
      sx={{
        minHeight: "469px",
        gap: theme.spacing(1),
      }}
      fullWidth>
      <Row
        type="cc"
        sx={{
          gap: theme.spacing(1),
          textAlign: "center",
        }}
        fullWidth>
        <Typography sx={{ marginX: "auto", paddingBottom: "100px" }} variant="mobile-h3">
          No calls yet
        </Typography>
      </Row>
    </Row>
  );
};

const DNAAttributeItem = ({ keyName, value }: { keyName: string; value: number }) => {
  const theme = useTheme();
  const { isTooltipOpen, setIsTooltipOpen, tooltipRef } = useTooltips();

  const handleTriggerClick = () => {
    setIsTooltipOpen(!isTooltipOpen);
  };

  return (
    <>
      <Card.Item sx={{ backdropFilter: "blur(10px)", width: "100%" }}>
        <Row type="bc" fullWidth>
          <Row type="sc" gap="4px">
            <Typography variant="mobile-body-s">
              {DNA_ATTRIBUTE_CONFIG_MAP[keyName as keyof typeof DNA_ATTRIBUTE_CONFIG_MAP].name}
            </Typography>
            <TooltipTrigger ref={tooltipRef} isOpen={isTooltipOpen} onClick={handleTriggerClick}>
              <Help sx={{ width: "16px", height: "16px" }} />
            </TooltipTrigger>
            <Row
              sx={{
                width: "6px",
                height: "6px",
                backgroundColor: "text.secondary",
                borderRadius: "50%",
                marginX: "4px",
              }}
            />
            <Typography variant="mobile-body-s" color="text.secondary">
              {getScoreLevel(value as number)}
            </Typography>
          </Row>
          <Row type="ss" gap="2px">
            {Array.from({
              length: Math.max(
                1,
                Math.floor((Math.min(value as number, MAX_TRAIT_SCORE) * 10) / MAX_TRAIT_SCORE),
              ),
            }).map((_, index) => (
              <Row
                key={index}
                width="8px"
                height="15px"
                sx={{
                  backgroundColor: theme.palette.surface.silver,
                  borderRadius: "84px",
                  animation: `colorFade .5s forwards`,
                  animationDelay: `${0.02 + index * 0.03}s`,
                  "@keyframes colorFade": {
                    "0%": { backgroundColor: theme.palette.surface.silver },
                    "100%": { backgroundColor: theme.palette.brand.primary },
                  },
                }}
              />
            ))}
            {Array.from({
              length: Math.min(
                9,
                Math.ceil(
                  ((MAX_TRAIT_SCORE - Math.min(value as number, MAX_TRAIT_SCORE)) * 10) /
                    MAX_TRAIT_SCORE,
                ),
              ),
            }).map((_, index) => (
              <Row
                key={index}
                width="8px"
                height="15px"
                sx={{
                  backgroundColor: theme.palette.surface.silver,
                  borderRadius: "84px",
                }}
              />
            ))}
          </Row>
        </Row>
      </Card.Item>
      <DefinitionTooltip
        term={DNA_ATTRIBUTE_CONFIG_MAP[
          keyName as keyof typeof DNA_ATTRIBUTE_CONFIG_MAP
        ].name.toUpperCase()}
        isShow={isTooltipOpen}
        maxWidth="480px">
        <Typography variant="body-default">
          {tooltipMap[keyName as keyof typeof tooltipMap]}
        </Typography>
      </DefinitionTooltip>
    </>
  );
};
export default GroupIdPage;
